import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { schedulerApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Play, 
  Settings, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  RefreshCw,
  Calendar,
  Activity,
  History
} from 'lucide-react'
import type { SchedulerConfigListItem, ExecutionHistory, JobType, JobStatus } from '@/types'

// Helper to format duration
function formatDuration(ms?: number): string {
  if (!ms) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

// Helper to format date
function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

// Helper to get status badge
function StatusBadge({ status }: { status?: JobStatus }) {
  if (!status) return <Badge variant="outline">Never Run</Badge>
  switch (status) {
    case 'SUCCESS':
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"><CheckCircle2 className="h-3 w-3 mr-1" />Success</Badge>
    case 'FAILED':
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>
    case 'RUNNING':
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Running</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

export default function SchedulerPage() {
  const queryClient = useQueryClient()
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<SchedulerConfigListItem | null>(null)
  const [cronExpression, setCronExpression] = useState('')
  const [isEnabled, setIsEnabled] = useState(true)
  const [historyFilter, setHistoryFilter] = useState<{ job_type?: JobType; status?: JobStatus }>({})

  // Fetch scheduler status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['scheduler-status'],
    queryFn: schedulerApi.getStatus,
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  // Fetch execution history
  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['scheduler-history', historyFilter],
    queryFn: () => schedulerApi.getHistory({ ...historyFilter, limit: 50 }),
    refetchInterval: 10000,
  })

  // Trigger job mutation
  const triggerMutation = useMutation({
    mutationFn: (jobType: JobType) => schedulerApi.triggerJob({ job_type: jobType }),
    onSuccess: (response) => {
      toast.success(response.message || 'Job triggered successfully')
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler-history'] })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to trigger job')
    },
  })

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: ({ jobType, data }: { jobType: JobType; data: { cron_expression?: string; is_enabled?: boolean } }) =>
      schedulerApi.updateConfig(jobType, data),
    onSuccess: () => {
      toast.success('Configuration updated successfully')
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] })
      setConfigDialogOpen(false)
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update configuration')
    },
  })

  const handleConfigClick = (job: SchedulerConfigListItem) => {
    setSelectedJob(job)
    setCronExpression(job.cron_expression)
    setIsEnabled(job.is_enabled)
    setConfigDialogOpen(true)
  }

  const handleSaveConfig = () => {
    if (!selectedJob) return
    updateConfigMutation.mutate({
      jobType: selectedJob.job_type,
      data: { cron_expression: cronExpression, is_enabled: isEnabled },
    })
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Scheduler Management</h1>
          <p className="text-muted-foreground">Manage scheduled background jobs and view execution history</p>
        </div>
        <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ['scheduler-status'] })}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Scheduler Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Activity className={`h-5 w-5 ${status?.is_running ? 'text-green-500' : 'text-red-500'}`} />
              <span className="text-2xl font-bold">{status?.is_running ? 'Running' : 'Stopped'}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{status?.jobs?.filter(j => j.is_enabled).length || 0}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Executions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-500" />
              <span className="text-2xl font-bold">{status?.total_executions || 0}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Executions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold">{status?.today_executions || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="jobs">Scheduled Jobs</TabsTrigger>
          <TabsTrigger value="history">Execution History</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Jobs</CardTitle>
              <CardDescription>Configure and manage scheduled background jobs</CardDescription>
            </CardHeader>
            <CardContent>
              {statusLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job Name</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead>Next Run</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {status?.jobs?.map((job) => (
                      <TableRow key={job.job_type}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{job.job_name}</div>
                            <div className="text-sm text-muted-foreground">{job.description}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{job.cron_expression}</code>
                        </TableCell>
                        <TableCell>
                          <Badge variant={job.is_enabled ? 'default' : 'secondary'}>
                            {job.is_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-sm">{formatDate(job.last_run_at)}</div>
                            <StatusBadge status={job.last_run_status} />
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(job.next_run_at)}</TableCell>
                        <TableCell>
                          {job.run_count > 0 ? (
                            <span className={job.success_count / job.run_count >= 0.9 ? 'text-green-600' : 'text-yellow-600'}>
                              {((job.success_count / job.run_count) * 100).toFixed(0)}%
                            </span>
                          ) : '-'}
                          <span className="text-muted-foreground text-sm ml-1">
                            ({job.success_count}/{job.run_count})
                          </span>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConfigClick(job)}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => triggerMutation.mutate(job.job_type)}
                            disabled={triggerMutation.isPending}
                          >
                            {triggerMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Execution History</CardTitle>
                  <CardDescription>View past job executions and their results</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select
                    value={historyFilter.status || 'all'}
                    onValueChange={(v) => setHistoryFilter(prev => ({ ...prev, status: v === 'all' ? undefined : v as JobStatus }))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="SUCCESS">Success</SelectItem>
                      <SelectItem value="FAILED">Failed</SelectItem>
                      <SelectItem value="RUNNING">Running</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job Type</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Triggered By</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history?.executions?.map((exec: ExecutionHistory) => (
                      <TableRow key={exec.execution_id}>
                        <TableCell className="font-medium">{exec.job_type}</TableCell>
                        <TableCell>{formatDate(exec.started_at)}</TableCell>
                        <TableCell>
                          {exec.completed_at
                            ? formatDuration(new Date(exec.completed_at).getTime() - new Date(exec.started_at).getTime())
                            : '-'}
                        </TableCell>
                        <TableCell><StatusBadge status={exec.status} /></TableCell>
                        <TableCell>
                          <Badge variant="outline">{exec.triggered_by}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-green-600">{exec.items_succeeded}</span>
                          {exec.items_failed > 0 && (
                            <span className="text-red-600 ml-1">/ {exec.items_failed} failed</span>
                          )}
                          <span className="text-muted-foreground ml-1">of {exec.items_processed}</span>
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={exec.error_message}>
                          {exec.error_message || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!history?.executions || history.executions.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No execution history found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Job: {selectedJob?.job_name}</DialogTitle>
            <DialogDescription>Update the schedule and status for this job</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cron">Cron Expression</Label>
              <Input
                id="cron"
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                placeholder="0 1 * * *"
              />
              <p className="text-sm text-muted-foreground">
                Format: minute hour day-of-month month day-of-week
              </p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="enabled">Enabled</Label>
              <Switch
                id="enabled"
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} disabled={updateConfigMutation.isPending}>
              {updateConfigMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

